import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { api } from "../services/api";
import { CartItem } from "../types";

interface PaymentResult {
	status: "succeeded" | "failed";
	transactionId: string;
	amountCents: number;
	taxCents: number;
	convenienceFeeCents: number;
	tipCents: number;
	totalCents: number;
	customerEmail?: string;
	completedAt: number;
	errorMessage?: string;
}

interface QrSession {
	sessionId: string;
	url: string;
	transactionId: string;
	expiresAt: number | null;
	qrDataUrl: string;
}

interface Props {
	items: CartItem[];
	subtotal: number;
	taxAmount: number;
	convenienceFee: number;
	tipAmount: number;
	total: number;
	onSuccess: () => void;
	onClose: () => void;
}

function formatCurrency(cents: number): string {
	return `$${(cents / 100).toFixed(2)}`;
}

// Poll the DB-only status endpoint until the Stripe webhook flips the row to
// a terminal state, or until we time out. Returns the final status, or "pending"
// if the webhook never arrived in time. The cancel signal lets the caller
// abort polling when the modal/tab is closed.
async function pollTransactionStatus(
	transactionId: string,
	{
		intervalMs = 1500,
		timeoutMs = 10 * 60 * 1000,
		signal,
	}: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {}
): Promise<"succeeded" | "failed" | "canceled" | "pending" | "aborted"> {
	const deadline = Date.now() + timeoutMs;
	while (Date.now() < deadline) {
		if (signal?.aborted) return "aborted";
		try {
			const { status } = await api.getTransactionStatus(transactionId);
			if (status !== "pending") return status;
		} catch {
			// Network blip — keep polling until deadline.
		}
		await new Promise((r) => setTimeout(r, intervalMs));
	}
	return "pending";
}

const COLORS = {
	primary: "#2563eb",
	primaryDark: "#1d4ed8",
	text: "#111827",
	textMuted: "#6B7280",
	textPlaceholder: "#9CA3AF",
	border: "#E5E7EB",
	card: "#FFFFFF",
	errorBg: "#FEE2E2",
	error: "#DC2626",
	successBg: "#DBEAFE",
	success: "#1d4ed8",
	radiusSm: 12,
	radiusMd: 14,
	radiusLg: 20,
};

export function QrPayment({
	items,
	subtotal,
	taxAmount,
	convenienceFee,
	tipAmount,
	total,
	onSuccess,
	onClose,
}: Props) {
	const [customerEmail, setCustomerEmail] = useState("");
	const [result, setResult] = useState<PaymentResult | null>(null);

	const [qrLoading, setQrLoading] = useState(false);
	const [qrSession, setQrSession] = useState<QrSession | null>(null);
	const [qrError, setQrError] = useState<string | null>(null);
	const [linkCopied, setLinkCopied] = useState(false);
	const qrPollAbort = useRef<AbortController | null>(null);
	const sessionStarted = useRef(false);

	// Create the Checkout Session once on mount and poll for the outcome.
	useEffect(() => {
		if (sessionStarted.current) return;
		sessionStarted.current = true;

		let cancelled = false;
		(async () => {
			setQrLoading(true);
			setQrError(null);
			try {
				const { checkoutSession, transaction } = await api.createCheckoutSession({
					items,
					tipAmount,
					customerEmail: customerEmail.trim() || undefined,
				});
				if (!checkoutSession.url) throw new Error("Stripe did not return a checkout URL");
				const qrDataUrl = await QRCode.toDataURL(checkoutSession.url, {
					margin: 1,
					width: 320,
					color: { dark: "#111827", light: "#FFFFFF" },
				});
				if (cancelled) return;
				setQrSession({
					sessionId: checkoutSession.id,
					url: checkoutSession.url,
					transactionId: transaction.id,
					expiresAt: checkoutSession.expiresAt,
					qrDataUrl,
				});

				const ctrl = new AbortController();
				qrPollAbort.current = ctrl;
				const status = await pollTransactionStatus(transaction.id, { signal: ctrl.signal });
				if (cancelled || status === "aborted") return;

				if (status === "succeeded") {
					setResult({
						status: "succeeded",
						transactionId: transaction.id,
						amountCents: subtotal,
						taxCents: taxAmount,
						convenienceFeeCents: convenienceFee,
						tipCents: tipAmount,
						totalCents: total,
						customerEmail: customerEmail.trim() || undefined,
						completedAt: Date.now(),
					});
				} else if (status === "failed" || status === "canceled") {
					setResult({
						status: "failed",
						transactionId: transaction.id,
						amountCents: subtotal,
						taxCents: taxAmount,
						convenienceFeeCents: convenienceFee,
						tipCents: tipAmount,
						totalCents: total,
						completedAt: Date.now(),
						errorMessage:
							status === "canceled"
								? "The customer canceled or the session expired."
								: "The payment did not go through.",
					});
				} else {
					// Timed out without a terminal state — leave the QR up,
					// surface a soft error, and let the cashier retry.
					setQrError("Still waiting on confirmation. Ask the customer to retry, or close and start over.");
				}
			} catch (err: any) {
				if (!cancelled) setQrError(err.message || "Could not create the QR session");
			} finally {
				if (!cancelled) setQrLoading(false);
			}
		})();

		return () => {
			cancelled = true;
			qrPollAbort.current?.abort();
			qrPollAbort.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// Cancel the session if the modal is unmounted while a QR session is
		// outstanding (and no terminal result was reached).
		return () => {
			if (qrSession && !result) {
				api.cancelCheckoutSession(qrSession.sessionId).catch(() => {});
			}
			qrPollAbort.current?.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleResultClose = () => {
		if (result?.status === "succeeded") {
			onSuccess();
		} else {
			onClose();
		}
	};

	const copyLink = async () => {
		if (!qrSession?.url) return;
		try {
			await navigator.clipboard.writeText(qrSession.url);
			setLinkCopied(true);
			setTimeout(() => setLinkCopied(false), 1800);
		} catch {
			// Clipboard unavailable — silently ignore.
		}
	};

	function formatDateTime(ms: number): string {
		return new Date(ms).toLocaleString();
	}

	const overlayStyle: React.CSSProperties = {
		position: "fixed",
		inset: 0,
		background: "rgba(15, 23, 42, 0.55)",
		backdropFilter: "blur(4px)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		zIndex: 1000,
		padding: 16,
	};

	const modalStyle: React.CSSProperties = {
		background: COLORS.card,
		borderRadius: COLORS.radiusLg,
		boxShadow: "0 20px 50px rgba(0, 0, 0, 0.25)",
		width: "100%",
		maxWidth: 460,
		maxHeight: "92vh",
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
	};

	if (result) {
		const succeeded = result.status === "succeeded";
		const accent = succeeded ? COLORS.success : COLORS.error;
		const accentBg = succeeded ? COLORS.successBg : COLORS.errorBg;

		const rowStyle: React.CSSProperties = {
			display: "flex",
			justifyContent: "space-between",
			alignItems: "baseline",
			padding: "8px 0",
			borderBottom: `1px solid ${COLORS.border}`,
			fontSize: 14,
		};
		const labelStyle: React.CSSProperties = { color: COLORS.textMuted };
		const valueStyle: React.CSSProperties = { color: COLORS.text, fontWeight: 600, textAlign: "right" };

		return (
			<div style={overlayStyle}>
				<div style={modalStyle} onClick={(e) => e.stopPropagation()}>
					<div
						style={{
							padding: "20px 20px 16px",
							borderBottom: `1px solid ${COLORS.border}`,
							textAlign: "center",
						}}>
						<div
							style={{
								width: 56,
								height: 56,
								borderRadius: "50%",
								background: accentBg,
								color: accent,
								margin: "0 auto 10px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: 30,
								fontWeight: 700,
							}}
							aria-hidden>
							{succeeded ? "✓" : "✕"}
						</div>
						<div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>
							{succeeded ? "Payment Successful" : "Payment Failed"}
						</div>
						<div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 4 }}>
							{succeeded
								? `${formatCurrency(result.totalCents)} charged via QR`
								: result.errorMessage || "The transaction could not be completed"}
						</div>
					</div>

					<div style={{ padding: "12px 20px 4px", overflowY: "auto", flex: 1 }}>
						<div style={rowStyle}>
							<span style={labelStyle}>Subtotal</span>
							<span style={valueStyle}>{formatCurrency(result.amountCents)}</span>
						</div>
						{result.taxCents > 0 && (
							<div style={rowStyle}>
								<span style={labelStyle}>Tax</span>
								<span style={valueStyle}>{formatCurrency(result.taxCents)}</span>
							</div>
						)}
						{result.convenienceFeeCents > 0 && (
							<div style={rowStyle}>
								<span style={labelStyle}>Convenience Fee (3%)</span>
								<span style={valueStyle}>{formatCurrency(result.convenienceFeeCents)}</span>
							</div>
						)}
						{result.tipCents > 0 && (
							<div style={rowStyle}>
								<span style={labelStyle}>Tip</span>
								<span style={valueStyle}>{formatCurrency(result.tipCents)}</span>
							</div>
						)}
						<div style={rowStyle}>
							<span style={labelStyle}>Total</span>
							<span style={{ ...valueStyle, color: accent }}>{formatCurrency(result.totalCents)}</span>
						</div>
						{result.customerEmail && (
							<div style={rowStyle}>
								<span style={labelStyle}>Receipt sent to</span>
								<span style={valueStyle}>{result.customerEmail}</span>
							</div>
						)}
						<div style={rowStyle}>
							<span style={labelStyle}>Date</span>
							<span style={valueStyle}>{formatDateTime(result.completedAt)}</span>
						</div>
						<div style={{ ...rowStyle, borderBottom: "none" }}>
							<span style={labelStyle}>Transaction ID</span>
							<span style={{ ...valueStyle, fontFamily: "monospace", fontSize: 12, wordBreak: "break-all", maxWidth: "60%" }}>
								{result.transactionId}
							</span>
						</div>
					</div>

					<div style={{ padding: 16, borderTop: `1px solid ${COLORS.border}` }}>
						<button
							onClick={handleResultClose}
							style={{
								width: "100%",
								padding: "13px 16px",
								background: accent,
								color: "#FFFFFF",
								border: "none",
								borderRadius: COLORS.radiusMd,
								fontSize: 15,
								fontWeight: 600,
								cursor: "pointer",
							}}>
							Close
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			style={overlayStyle}
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}>
			<div style={modalStyle} onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div
					style={{
						padding: "16px 20px",
						borderBottom: `1px solid ${COLORS.border}`,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}>
					<div>
						<div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>Pay by QR</div>
						<div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
							Charge {formatCurrency(total)}
						</div>
					</div>
					<button
						onClick={onClose}
						style={{
							background: "none",
							border: "none",
							fontSize: 22,
							color: COLORS.textMuted,
							cursor: "pointer",
							padding: 4,
							lineHeight: 1,
						}}
						aria-label="Close">
						×
					</button>
				</div>

				{/* Body */}
				<div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
					<div style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "stretch" }}>
						<div style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center" }}>
							Have the customer scan this code with their phone camera. They'll pay with Apple Pay, Google Pay, Link, or any card.
						</div>

						{qrError && !qrSession && (
							<div style={{ padding: 12, background: COLORS.errorBg, color: COLORS.error, borderRadius: COLORS.radiusSm, fontSize: 13 }}>
								{qrError}
							</div>
						)}

						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								padding: 16,
								background: "#F9FAFB",
								border: `1px solid ${COLORS.border}`,
								borderRadius: COLORS.radiusMd,
								minHeight: 280,
							}}>
							{qrLoading && !qrSession ? (
								<div style={{ color: COLORS.textMuted, fontSize: 13 }}>Generating QR code…</div>
							) : qrSession ? (
								<img
									src={qrSession.qrDataUrl}
									alt="Scan to pay"
									style={{ width: 256, height: 256, display: "block" }}
								/>
							) : (
								<div style={{ color: COLORS.textMuted, fontSize: 13 }}>
									{qrError ? "Couldn't generate a code." : "Preparing…"}
								</div>
							)}
						</div>

						{qrSession && (
							<>
								<div
									style={{
										fontSize: 12,
										color: COLORS.textMuted,
										textAlign: "center",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 8,
									}}>
									<span
										style={{
											width: 8,
											height: 8,
											borderRadius: "50%",
											background: COLORS.primary,
											boxShadow: `0 0 0 4px ${COLORS.successBg}`,
											animation: "pulse 1.5s infinite",
										}}
									/>
									<span>Waiting for the customer to pay…</span>
								</div>

								<label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
									<span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
										Email for receipt <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>(optional)</span>
									</span>
									<input
										type="email"
										autoComplete="email"
										value={customerEmail}
										onChange={(e) => setCustomerEmail(e.target.value)}
										placeholder="customer@example.com"
										style={{
											padding: "10px 12px",
											border: `1px solid ${COLORS.border}`,
											borderRadius: COLORS.radiusSm,
											fontSize: 15,
											background: COLORS.card,
										}}
									/>
								</label>

								<div style={{ display: "flex", gap: 8 }}>
									<button
										onClick={copyLink}
										style={{
											flex: 1,
											padding: "10px 12px",
											background: "#FFFFFF",
											color: COLORS.text,
											border: `1px solid ${COLORS.border}`,
											borderRadius: COLORS.radiusSm,
											fontSize: 13,
											fontWeight: 600,
											cursor: "pointer",
										}}>
										{linkCopied ? "Link copied" : "Copy link"}
									</button>
									<button
										onClick={onClose}
										style={{
											flex: 1,
											padding: "10px 12px",
											background: "#FFFFFF",
											color: COLORS.error,
											border: `1px solid ${COLORS.border}`,
											borderRadius: COLORS.radiusSm,
											fontSize: 13,
											fontWeight: 600,
											cursor: "pointer",
										}}>
										Cancel
									</button>
								</div>

								{qrError && (
									<div style={{ padding: 10, background: COLORS.errorBg, color: COLORS.error, borderRadius: COLORS.radiusSm, fontSize: 13 }}>
										{qrError}
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>
			<style>{`
				@keyframes pulse {
					0%, 100% { opacity: 1; }
					50% { opacity: 0.35; }
				}
			`}</style>
		</div>
	);
}
